import { appAlert, appConfirm, appPrompt } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// ClinicalRecord.js — Clinical interactions engine (v1, localStorage sandbox).
//
//  • Patients (demographics + chronological record). Later: pharmace_common_db
//    keyed by NHS number so records follow patients between tenants.
//  • Consultations: free entry OR template-driven. Components are typed —
//    Question / Examination / Assessment / Action (QEAX) — colour-coded and
//    stored as structured data (type survives into search + future SNOMED).
//  • Header (trigger) + Outcome (disposal + diagnoses).
//  • AMENDMENT MODEL: edits allowed; every edit snapshots the full prior
//    record into versions[]; amended records render on AMBER with
//    "view original" per snapshot. Nothing is ever silently overwritten.
//  • Templates at 4 levels (pharmacist / pharmacy / organisation / global)
//    with author, change log, target users; create/update FROM a live
//    consultation; document converter (paste a PGD/procedure → draft
//    template) — heuristic now, platform-AI later.
//  • SNOMED: off-by-default toggle; "Suggest codes" chips on assessments &
//    diagnoses (accept/dismiss; codes attach alongside text, never replace).
//    Starter subset embedded; swap for the TRUD UK Edition subset later.
//  • Contextual suggestions while typing component labels (from the
//    template library) — suggestions, never autocompletion.

import React, { useEffect, useMemo, useRef, useState } from "react";
const LS = {
  get(k, d) {
    try {
      return JSON.parse(localStorage.getItem(k)) ?? d;
    } catch {
      return d;
    }
  },
  set(k, v) {
    localStorage.setItem(k, JSON.stringify(v));
  }
};
const K = {
  patients: "cr_patients",
  records: "cr_records",
  templates: "cr_templates",
  settings: "cr_settings"
};
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const now = () => new Date().toISOString();
const me = () => localStorage.getItem("userName") || "Pharmacist";
const fmtDT = d => d ? new Date(d).toLocaleString("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
}) : "—";
const API = apiUrl("");
async function aiPost(path, body) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const TYPES = {
  question: {
    label: "Question",
    bg: "#E6F7FF",
    border: "#91D5FF",
    text: "#0958D9"
  },
  examination: {
    label: "Examination",
    bg: "#F6FFED",
    border: "#B7EB8F",
    text: "#389E0D"
  },
  assessment: {
    label: "Assessment",
    bg: "#FFFBE6",
    border: "#FFE58F",
    text: "#AD6800"
  },
  action: {
    label: "Action",
    bg: "#fae8ff",
    border: "#e9d5ff",
    text: "#6b21a8"
  }
};
const DISPOSALS = ["Advice only", "Medicine supplied (OTC)", "Supplied under PGD", "Referred to GP (routine)", "Referred to GP (urgent)", "Referred to A&E / 999", "Referred to other service", "Declined / did not proceed", "Follow-up booked"];
const SNOMED = [["65363002", "Otitis media"], ["3110003", "Acute otitis externa"], ["18099001", "Earwax impaction"], ["68566005", "Urinary tract infection"], ["405737000", "Pharyngitis"], ["43878008", "Streptococcal sore throat"], ["36971009", "Sinusitis"], ["48277006", "Impetigo"], ["4740000", "Herpes zoster (shingles)"], ["24079001", "Atopic dermatitis"], ["195967001", "Asthma"], ["38341003", "Hypertension"], ["73211009", "Diabetes mellitus"], ["16114001", "Otalgia"], ["25064002", "Headache"], ["386661006", "Fever"], ["49727002", "Cough"], ["267036007", "Dyspnoea"], ["21522001", "Abdominal pain"], ["62315008", "Diarrhoea"], ["422400008", "Vomiting"], ["271807003", "Skin rash"], ["70076002", "Rhinitis"], ["162397003", "Sore throat symptom"], ["84229001", "Fatigue"], ["57676002", "Arthralgia"], ["68962001", "Myalgia"], ["386705008", "Lightheadedness"], ["247441003", "Erythema"], ["65124004", "Swelling"], ["419076005", "Allergic reaction"], ["185389009", "Ear syringing"], ["50849002", "Emergency contraception"], ["266707007", "Smoking cessation advice"], ["170836005", "Blood pressure check"], ["43396009", "HbA1c measurement"], ["225323000", "Wound dressing"], ["33879002", "Influenza vaccination"]];
function snomedSuggest(text) {
  const t = (text || "").toLowerCase().trim();
  if (t.length < 3) return [];
  return SNOMED.filter(([, term]) => term.toLowerCase().includes(t) || t.split(/\s+/).some(w => w.length > 3 && term.toLowerCase().includes(w))).slice(0, 5);
}
function seedTemplates() {
  if (LS.get(K.templates, null)) return;
  LS.set(K.templates, [{
    id: uid(),
    name: "Ear examination & wax removal",
    level: "global",
    author: "Pharmace",
    targetUsers: "All organisations",
    triggerHint: "Patient presents with blocked ear / hearing loss",
    version: 1,
    createdAt: now(),
    changeLog: [{
      at: now(),
      by: "Pharmace",
      note: "Seeded starter template"
    }],
    components: [{
      type: "question",
      label: "Duration of symptoms and which ear(s)?"
    }, {
      type: "question",
      label: "Any pain, discharge, or previous ear surgery?"
    }, {
      type: "question",
      label: "Any hearing aids or previous wax removal?"
    }, {
      type: "examination",
      label: "Otoscopy — tympanic membrane, canal, wax appearance"
    }, {
      type: "assessment",
      label: "Assessment — wax impaction / other pathology"
    }, {
      type: "action",
      label: "Procedure performed / advice given / referral"
    }]
  }]);
}
const S = {
  input: {
    fontSize: 13,
    padding: "6px 10px",
    border: "1px solid #8C8C8C",
    borderRadius: 4,
    boxSizing: "border-box"
  },
  btn: (c, solid) => ({
    border: `1px solid ${c}`,
    color: solid ? "#fff" : c,
    background: solid ? c : "#fff",
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer"
  }),
  chip: (bg, bd, fg) => ({
    background: bg,
    border: `1px solid ${bd}`,
    color: fg,
    borderRadius: 999,
    padding: "2px 10px",
    fontSize: 11,
    fontWeight: 700
  }),
  lab: {
    fontSize: 11,
    fontWeight: 700,
    color: "#5D5D5D",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    margin: "10px 0 4px"
  }
};
export default function ClinicalRecord() {
  seedTemplates();
  const [tab, setTab] = useState("patients");
  const [patients, setPatients] = useState(() => LS.get(K.patients, []));
  const [records, setRecords] = useState(() => LS.get(K.records, []));
  const [templates, setTemplates] = useState(() => LS.get(K.templates, []));
  const [settings, setSettings] = useState(() => LS.get(K.settings, {
    snomedSuggest: false
  }));
  const [patient, setPatient] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => LS.set(K.patients, patients), [patients]);
  useEffect(() => LS.set(K.records, records), [records]);
  useEffect(() => LS.set(K.templates, templates), [templates]);
  useEffect(() => LS.set(K.settings, settings), [settings]);
  return <div style={{
    padding: "16px 20px"
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8
    }}>
        <div>
          <h2 style={{
          margin: 0,
          fontSize: 20
        }}>Clinical record <span style={{
            fontSize: 11,
            color: "#AD4E00",
            background: "#FFF7E6",
            border: "1px solid #FFD666",
            borderRadius: 999,
            padding: "2px 8px",
            verticalAlign: "middle"
          }}>SANDBOX — local storage</span></h2>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>Patients, consultations and templates live in this browser only while we experiment. Production storage will be pharmace_common_db keyed by NHS number.</div>
        </div>
        <label style={{
        fontSize: 12,
        color: "#434343",
        display: "flex",
        gap: 6,
        alignItems: "center"
      }}>
          <input type="checkbox" checked={!!settings.snomedSuggest} onChange={e => setSettings(s => ({
          ...s,
          snomedSuggest: e.target.checked
        }))} />
          SNOMED code suggestions
        </label>
      </div>

      <div style={{
      display: "flex",
      gap: 4,
      margin: "12px 0",
      borderBottom: "1px solid #F0F0F0"
    }}>
        {/* Converter tab HIDDEN for now — Converter component + "convert"
            view render below are intact; re-add the entry here to re-enable. */}
        {[["patients", "Patients & records"], ["templates", "Templates"]].map(([k, l]) => <button key={k} onClick={() => {
        setTab(k);
        setPatient(null);
        setEditing(null);
      }} style={{
        border: "none",
        borderBottom: tab === k ? "3px solid #0E8CFF" : "3px solid transparent",
        background: "none",
        padding: "7px 14px",
        fontSize: 13,
        fontWeight: tab === k ? 800 : 500,
        color: "#262626",
        cursor: "pointer",
        fontFamily: "inherit"
      }}>{l}</button>)}
      </div>

      {tab === "patients" && !patient && <PatientList patients={patients} setPatients={setPatients} records={records} onOpen={setPatient} />}
      {tab === "patients" && patient && !editing && <PatientTimeline patient={patient} records={records} setRecords={setRecords} templates={templates} onBack={() => setPatient(null)} onNew={tpl => setEditing(newConsultation(patient, tpl))} onEdit={rec => setEditing({
      ...JSON.parse(JSON.stringify(rec)),
      _isEdit: true
    })} />}
      {tab === "patients" && patient && editing && <ConsultationEditor patient={patient} draft={editing} setDraft={setEditing} templates={templates} setTemplates={setTemplates} settings={settings} onCancel={() => setEditing(null)} onSave={rec => {
      setRecords(rs => {
        const i = rs.findIndex(r => r.id === rec.id);
        if (i === -1) return [...rs, rec];
        const prior = rs[i];
        const snapshot = {
          ...prior
        };
        delete snapshot.versions;
        const updated = {
          ...rec,
          versions: [...(prior.versions || []), {
            snapshot,
            editedAt: now(),
            editedBy: me()
          }],
          editedAt: now()
        };
        return rs.map((r, j) => j === i ? updated : r);
      });
      setEditing(null);
    }} />}
      {tab === "templates" && <TemplateManager templates={templates} setTemplates={setTemplates} />}
      {tab === "convert" && <Converter onSave={tpl => {
      setTemplates(ts => [...ts, tpl]);
      setTab("templates");
    }} />}
    </div>;
}
function newConsultation(patient, tpl) {
  return {
    id: uid(),
    patientId: patient.id,
    createdAt: now(),
    createdBy: me(),
    templateId: tpl?.id || null,
    templateName: tpl?.name || null,
    trigger: tpl?.triggerHint || "",
    components: (tpl?.components || []).map(c => ({
      id: uid(),
      type: c.type,
      label: c.label,
      value: "",
      codes: []
    })),
    outcome: {
      disposal: "",
      diagnoses: [],
      notes: ""
    },
    versions: []
  };
}
function PatientList({
  patients,
  setPatients,
  records,
  onOpen
}) {
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const filtered = patients.filter(p => !q || `${p.firstName} ${p.lastName} ${p.nhsNumber || ""}`.toLowerCase().includes(q.toLowerCase()));
  const save = () => {
    if (!form.firstName || !form.lastName) return;
    setPatients(ps => form.id ? ps.map(p => p.id === form.id ? form : p) : [...ps, {
      ...form,
      id: uid(),
      createdAt: now()
    }]);
    setForm(null);
  };
  return <div>
      <div style={{
      display: "flex",
      gap: 8,
      marginBottom: 10
    }}>
        <input style={{
        ...S.input,
        width: 280
      }} placeholder="Search name / NHS number…" value={q} onChange={e => setQ(e.target.value)} />
        <button style={S.btn("#0E8CFF", true)} onClick={() => setForm({
        firstName: "",
        lastName: "",
        dob: "",
        sex: "",
        nhsNumber: "",
        phone: "",
        address: ""
      })}>+ New patient</button>
      </div>
      {form && <div style={{
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      padding: 14,
      marginBottom: 12,
      background: "#FCFCFC"
    }}>
          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
        gap: 8
      }}>
            {[["firstName", "First name"], ["lastName", "Last name"], ["dob", "DOB (dd/mm/yyyy)"], ["sex", "Sex"], ["nhsNumber", "NHS number"], ["phone", "Phone"]].map(([k, l]) => <label key={k} style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          fontSize: 12,
          color: "#434343"
        }}>{l}
                <input style={S.input} value={form[k] || ""} onChange={e => setForm(f => ({
            ...f,
            [k]: e.target.value
          }))} /></label>)}
          </div>
          <label style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontSize: 12,
        color: "#434343",
        marginTop: 8
      }}>Address
            <input style={S.input} value={form.address || ""} onChange={e => setForm(f => ({
          ...f,
          address: e.target.value
        }))} /></label>
          <div style={{
        marginTop: 10,
        display: "flex",
        gap: 8
      }}>
            <button style={S.btn("#0E8CFF", true)} onClick={save}>Save patient</button>
            <button style={S.btn("#5D5D5D")} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>}
      {filtered.length === 0 && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>No patients yet — add one to start recording consultations.</div>}
      {filtered.map(p => {
      const count = records.filter(r => r.patientId === p.id).length;
      return <div key={p.id} onClick={() => onOpen(p)} style={{
        display: "flex",
        justifyContent: "space-between",
        border: "1px solid #F0F0F0",
        borderRadius: 6,
        padding: "8px 12px",
        marginBottom: 5,
        cursor: "pointer",
        background: "#fff"
      }}>
            <span style={{
          fontSize: 14
        }}><b>{(p.lastName || "").toUpperCase()}, {p.firstName}</b> <span style={{
            color: "#5D5D5D",
            fontSize: 12
          }}> {p.dob} · NHS {p.nhsNumber || "—"}</span></span>
            <span style={{
          display: "flex",
          gap: 8,
          alignItems: "center"
        }}>
              <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>{count} consultation{count === 1 ? "" : "s"}</span>
              <button style={S.btn("#5D5D5D")} onClick={e => {
            e.stopPropagation();
            setForm(p);
          }}>Edit</button>
            </span>
          </div>;
    })}
    </div>;
}
function PatientTimeline({
  patient,
  records,
  setRecords,
  templates,
  onBack,
  onNew,
  onEdit
}) {
  const [q, setQ] = useState("");
  const [by, setBy] = useState("");
  const [tpl, setTpl] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [viewVersions, setViewVersions] = useState(null);
  const [auditRec, setAuditRec] = useState(null);
  const [showSum, setShowSum] = useState(false);
  const [sumBusy, setSumBusy] = useState({});

  // Generate/refresh AI synopses for the given records (sequential; cached
  // on the record, regenerated only after an amendment makes it stale).
  const generateSummaries = async recs => {
    for (const r of recs) {
      const stale = r.synopsis && r.editedAt && new Date(r.editedAt) > new Date(r.synopsis.at);
      if (r.synopsis && !stale) continue;
      setSumBusy(b => ({
        ...b,
        [r.id]: true
      }));
      try {
        const d = await aiPost("/api/clinical/ai/consultation-synopsis", {
          trigger: r.trigger,
          components: r.components,
          outcome: r.outcome
        });
        setRecords(rs => rs.map(x => x.id === r.id ? {
          ...x,
          synopsis: {
            text: d.synopsis,
            at: new Date().toISOString()
          }
        } : x));
      } catch (e) {
        setRecords(rs => rs.map(x => x.id === r.id ? {
          ...x,
          synopsis: {
            text: "(summary unavailable: " + e.message + ")",
            at: new Date().toISOString(),
            error: true
          }
        } : x));
      } finally {
        setSumBusy(b => ({
          ...b,
          [r.id]: false
        }));
      }
    }
  };
  const [pickTpl, setPickTpl] = useState(false);
  const [collapsedAll, setCollapsedAll] = useState(true); // consultations start collapsed
  const [open, setOpen] = useState({}); // per-record override of the master state
  const isOpen = id => open[id] !== undefined ? open[id] : !collapsedAll;
  const toggle = id => setOpen(o => ({
    ...o,
    [id]: !isOpen(id)
  }));
  const mine = records.filter(r => r.patientId === patient.id).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const enterers = [...new Set(mine.map(r => r.createdBy))];
  const filtered = mine.filter(r => {
    if (by && r.createdBy !== by) return false;
    if (tpl && (r.templateName || "(free entry)") !== tpl) return false;
    if (from && new Date(r.createdAt) < new Date(from)) return false;
    if (to && new Date(r.createdAt) > new Date(to + "T23:59:59")) return false;
    if (q) {
      const hay = [r.trigger, r.outcome?.notes, r.outcome?.disposal, r.templateName, ...(r.outcome?.diagnoses || []).map(d => d.text), ...r.components.map(c => c.label + " " + c.value)].join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  // v-scroll: the entries list lives in its OWN scroll box sized to the
  // remaining viewport, so the patient header, action buttons and filter
  // row above it stay permanently on screen. Height is measured from the
  // box's live top edge (robust to the page chrome above) and re-measured
  // on window resize and when the filter row wraps.
  const listRef = React.useRef(null);
  const [listH, setListH] = React.useState(null);
  React.useEffect(() => {
    const measure = () => {
      if (!listRef.current) return;
      const top = listRef.current.getBoundingClientRect().top;
      setListH(Math.max(220, window.innerHeight - top - 14));
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 50); // after fonts/wrap settle
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  });
  return <div>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8
    }}>
        <div>
          <button style={S.btn("#5D5D5D")} onClick={onBack}>← Patients</button>
          <span style={{
          fontSize: 16,
          fontWeight: 800,
          marginLeft: 10
        }}>{(patient.lastName || "").toUpperCase()}, {patient.firstName}</span>
          <span style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginLeft: 8
        }}>{patient.dob} · NHS {patient.nhsNumber || "—"}</span>
        </div>
        <div style={{
        display: "flex",
        gap: 6,
        position: "relative"
      }}>
          <button style={S.btn("#1D39C4")} onClick={() => {
          const turningOn = !showSum;
          setShowSum(turningOn);
          if (turningOn) generateSummaries(filtered);
        }} title="AI-generated 2-4 line synopsis beneath each consultation — key findings and outcome. Works alongside expand: both can be shown at once.">
            {showSum ? "Hide summaries" : "Show summaries"}</button>
          <button style={S.btn("#5D5D5D")} onClick={() => {
          setCollapsedAll(c => !c);
          setOpen({});
        }} title="Collapse to one summary line per consultation, or expand all.">
            {collapsedAll ? "▸ Expand all" : "▾ Collapse all"}</button>
          <button style={S.btn("#0E8CFF", true)} onClick={() => onNew(null)}>+ Free entry</button>
          <button style={S.btn("#0E8CFF")} onClick={() => setPickTpl(v => !v)}>+ From template ▾</button>
          {pickTpl && <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          right: 0,
          width: 380,
          maxHeight: 320,
          overflowY: "auto",
          background: "#fff",
          border: "1px solid #D9D9D9",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          zIndex: 50
        }}>
              {templates.length === 0 && <div style={{
            padding: 10,
            fontSize: 13,
            color: "#5D5D5D"
          }}>No templates yet.</div>}
              {templates.map(t => <div key={t.id} onClick={() => {
            setPickTpl(false);
            onNew(t);
          }} style={{
            padding: "7px 12px",
            cursor: "pointer",
            fontSize: 13,
            borderBottom: "1px solid #F5F5F5"
          }} onMouseEnter={e => e.currentTarget.style.background = "#E6F7FF"} onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                  <b>{t.name}</b> <span style={S.chip("#F5F5F5", "#D9D9D9", "#434343")}>{t.level}</span>
                  <div style={{
              color: "#5D5D5D",
              fontSize: 11
            }}>{t.components.length} components · v{t.version}</div>
                </div>)}
            </div>}
        </div>
      </div>
      <div style={{
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      margin: "10px 0"
    }}>
        <input style={{
        ...S.input,
        width: 220
      }} placeholder="Keyword search…" value={q} onChange={e => setQ(e.target.value)} />
        <select style={S.input} value={by} onChange={e => setBy(e.target.value)}><option value="">Any clinician</option>{enterers.map(e2 => <option key={e2}>{e2}</option>)}</select>
        <select style={S.input} value={tpl} onChange={e => setTpl(e.target.value)}><option value="">Any template</option><option>(free entry)</option>{[...new Set(mine.map(r => r.templateName).filter(Boolean))].map(t => <option key={t}>{t}</option>)}</select>
        <input type="date" style={S.input} value={from} onChange={e => setFrom(e.target.value)} />
        <input type="date" style={S.input} value={to} onChange={e => setTo(e.target.value)} />
      </div>

      <div ref={listRef} style={{
      height: listH ? `${listH}px` : "auto",
      overflowY: "auto",
      overflowX: "hidden",
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      background: "#FAFAFA",
      padding: "10px 10px 4px"
    }}>
      {filtered.map(r => {
        const amended = (r.versions || []).length > 0;
        return <div key={r.id} style={{
          border: `1px solid ${amended ? "#FA8C16" : "#F0F0F0"}`,
          background: amended ? "#FFFBE6" : "#fff",
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 10
        }}>
            <div style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 6
          }}>
              <div onClick={() => toggle(r.id)} style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#262626",
              cursor: "pointer",
              lineHeight: 1.5
            }} title="Click to expand / collapse this consultation.">
                <span style={{
                color: "#5D5D5D",
                fontWeight: 800,
                marginRight: 4
              }}>{isOpen(r.id) ? "▾" : "▸"}</span>
                <b style={{
                fontWeight: 800
              }}>{fmtDT(r.createdAt)}</b> · {r.createdBy} · {r.templateName ? <>template: <b style={{
                  fontWeight: 800
                }}>{r.templateName}</b></> : (r.trigger || "").trim() ? <b style={{
                fontWeight: 800
              }}>{r.trigger.length > 70 ? r.trigger.slice(0, 70) + "…" : r.trigger}</b> : "free entry"}
                {r.outcome?.disposal && <> · <span style={{
                  fontWeight: 800
                }}>Outcome:</span> {r.outcome.disposal}</>}
                {(r.outcome?.diagnoses || []).length > 0 && <> · <span style={{
                  fontWeight: 800
                }}>Diagnoses:</span> {r.outcome.diagnoses.map(d => d.text).join("; ")}</>}
                {amended && <span style={{
                ...S.chip("#FFF7E6", "#FFD666", "#AD4E00"),
                marginLeft: 8,
                verticalAlign: "middle"
              }}>AMENDED {fmtDT(r.editedAt)}</span>}
              </div>
              <div style={{
              display: "flex",
              gap: 6
            }}>
                {amended && <button style={S.btn("#AD4E00")} onClick={() => setViewVersions(r)}>View original{r.versions.length > 1 ? "s" : ""} ({r.versions.length})</button>}
                <button style={S.btn("#4338ca")} onClick={() => onEdit(r)}>Edit</button>
                <button style={S.btn("#0E8CFF")} onClick={() => setAuditRec(r)}>Audit</button>
              </div>
            </div>
            {showSum && <div style={{
            margin: "5px 0 2px",
            padding: "6px 10px",
            background: "#eef2ff",
            border: "1px solid #c7d2fe",
            borderRadius: 6,
            fontSize: 12.5,
            fontStyle: "italic",
            color: "#312e81",
            whiteSpace: "pre-wrap"
          }}>
                {sumBusy[r.id] ? "Generating summary…" : r.synopsis?.text || "Summary pending…"}
              </div>}
            {isOpen(r.id) && <>
              {r.trigger && r.templateName && <div style={{
              fontSize: 13,
              margin: "6px 0",
              color: "#262626"
            }}><b>Presenting / trigger:</b> {r.trigger}</div>}
              {r.components.filter(c => c.value || c.label).map(c => <ComponentView key={c.id} c={c} />)}
              {r.outcome?.notes && <div style={{
              fontSize: 13,
              color: "#434343",
              marginTop: 4
            }}>{r.outcome.notes}</div>}
            </>}
          </div>;
      })}
      {filtered.length === 0 && <div style={{
        color: "#5D5D5D",
        fontSize: 13,
        padding: 6
      }}>No consultations match.</div>}
      </div>

      {auditRec && (() => {
      const notDone = auditRec.components.filter(c => c.label && (!(c.value || "").trim() || c.value === "(not necessary)"));
      const ai = auditRec.aiReview;
      return <div onClick={() => setAuditRec(null)} style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200
      }}>
            <div onClick={e => e.stopPropagation()} style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(700px,94vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          padding: 16
        }}>
              <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8
          }}>
                <b>Audit — {fmtDT(auditRec.createdAt)}</b>
                <button style={S.btn("#5D5D5D")} onClick={() => setAuditRec(null)}>✕ Close</button>
              </div>
              <div style={S.lab}>Change log</div>
              <div style={{
            fontSize: 13
          }}>Created {fmtDT(auditRec.createdAt)} by {auditRec.createdBy}{auditRec.templateName ? ` — from template "${auditRec.templateName}"` : " — free entry"}</div>
              {(auditRec.versions || []).map((v, i) => <div key={i} style={{
            fontSize: 13
          }}>Amendment {i + 1}: prior version superseded {fmtDT(v.editedAt)} by {v.editedBy} (snapshot viewable via "View originals")</div>)}
              {(auditRec.versions || []).length === 0 && <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>No amendments — record as originally saved.</div>}

              <div style={S.lab}>Items not completed / marked not necessary</div>
              {notDone.length === 0 ? <div style={{
            fontSize: 13,
            color: "#389E0D"
          }}>All prompted items were answered.</div> : notDone.map(c => <div key={c.id} style={{
            fontSize: 13
          }}>• [{TYPES[c.type]?.label}] {c.label} — <i>{c.value === "(not necessary)" ? "marked not necessary" : "left blank"}</i></div>)}

              <div style={S.lab}>AI review at point of save</div>
              {!ai ? <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>No AI review stored (saved before the AI gate existed, or review skipped).</div> : ai.error ? <div style={{
            fontSize: 13,
            color: "#AD4E00"
          }}>AI review was unavailable at save: {ai.error}</div> : <div style={{
            fontSize: 13
          }}>
                    <div style={{
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              borderRadius: 6,
              padding: "6px 10px"
            }}>{ai.summary || "No comments."}</div>
                    {ai.concerns?.length > 0 && <><b style={{
                color: "#A8071A"
              }}>Concerns:</b> {ai.concerns.map((m, i) => <div key={i}>• {m}</div>)}</>}
                    {ai.overlooked?.length > 0 && <><b style={{
                color: "#AD4E00"
              }}>Possibly overlooked:</b> {ai.overlooked.map((m, i) => <div key={i}>• {m}</div>)}</>}
                    <div style={{
              color: "#8C8C8C",
              fontSize: 11,
              marginTop: 4
            }}>Reviewed {fmtDT(ai.at)}</div>
                  </div>}
            </div>
          </div>;
    })()}

      {viewVersions && <div onClick={() => setViewVersions(null)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1200
    }}>
          <div onClick={e => e.stopPropagation()} style={{
        background: "#fff",
        borderRadius: 8,
        width: "min(760px,94vw)",
        maxHeight: "85vh",
        overflowY: "auto",
        padding: 16
      }}>
            <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8
        }}>
              <b>Version history — original record{viewVersions.versions.length > 1 ? "s" : ""}</b>
              <button style={S.btn("#5D5D5D")} onClick={() => setViewVersions(null)}>✕ Close</button>
            </div>
            {viewVersions.versions.map((v, i) => <div key={i} style={{
          border: "1px solid #F0F0F0",
          borderRadius: 6,
          padding: 10,
          marginBottom: 8
        }}>
                <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginBottom: 6
          }}>Version {i + 1} — superseded {fmtDT(v.editedAt)} by {v.editedBy}. Read-only snapshot.</div>
                {v.snapshot.trigger && <div style={{
            fontSize: 13
          }}><b>Trigger:</b> {v.snapshot.trigger}</div>}
                {v.snapshot.components.map(c => <ComponentView key={c.id} c={c} />)}
                <OutcomeView outcome={v.snapshot.outcome} />
              </div>)}
          </div>
        </div>}
    </div>;
}
function ComponentView({
  c
}) {
  const t = TYPES[c.type] || TYPES.question;
  return <div style={{
    background: t.bg,
    border: `1px solid ${t.border}`,
    borderRadius: 6,
    padding: "4px 8px",
    margin: "3px 0",
    fontSize: 13,
    lineHeight: 1.5,
    display: "flex",
    gap: 6,
    alignItems: "stretch"
  }}>
      <span style={{
      ...S.chip("#ffffffaa", t.border, t.text),
      width: 92,
      textAlign: "center",
      flex: "0 0 auto",
      alignSelf: "flex-start",
      marginTop: 3
    }}>{t.label}</span>
      <div style={{
      flex: "0 0 25%",
      background: "#ffffffb3",
      borderRadius: 4,
      padding: "3px 8px",
      fontWeight: 600,
      fontStyle: "italic",
      color: t.text
    }}>
        {c.label}
      </div>
      <div style={{
      flex: 1,
      background: "rgba(0,0,0,0.07)",
      borderRadius: 4,
      padding: "3px 8px",
      color: "#262626",
      whiteSpace: "pre-wrap"
    }}>
        {c.value || <span style={{
        color: "#8C8C8C"
      }}>—</span>}
        {(c.codes || []).map(cd => <span key={cd.id} style={{
        ...S.chip("#eef2ff", "#c7d2fe", "#1D39C4"),
        marginLeft: 6
      }}>SNOMED {cd.id} · {cd.term}</span>)}
      </div>
    </div>;
}
function OutcomeView({
  outcome
}) {
  if (!outcome || !outcome.disposal && !(outcome.diagnoses || []).length && !outcome.notes) return null;
  return <div style={{
    borderTop: "1px dashed #D9D9D9",
    marginTop: 6,
    paddingTop: 6,
    fontSize: 13
  }}>
      <b>Outcome:</b> {outcome.disposal || "—"}
      {(outcome.diagnoses || []).length > 0 && <> · <b>Diagnoses:</b> {outcome.diagnoses.map((d, i) => <span key={i}>{d.text}{(d.codes || []).map(c => <span key={c.id} style={{
          ...S.chip("#eef2ff", "#c7d2fe", "#1D39C4"),
          marginLeft: 4
        }}>{c.id}</span>)}{i < outcome.diagnoses.length - 1 ? "; " : ""}</span>)}</>}
      {outcome.notes && <div style={{
      color: "#434343",
      marginTop: 2
    }}>{outcome.notes}</div>}
    </div>;
}
function ConsultationEditor({
  patient,
  draft,
  setDraft,
  templates,
  setTemplates,
  settings,
  onSave,
  onCancel
}) {
  const [suggestFor, setSuggestFor] = useState(null);
  const [codeSuggest, setCodeSuggest] = useState({});
  const [unansweredIds, setUnansweredIds] = useState(null); // popup: ids empty at Save time
  const [safetyErr, setSafetyErr] = useState(false);
  const [aiGate, setAiGate] = useState(null); // { status: 'loading'|'ready'|'error', draft, result }
  const valRefs = useRef({});
  const doSave = (d, aiReview) => {
    const rec = {
      ...d,
      aiReview: aiReview || null
    };
    delete rec._isEdit;
    onSave(rec);
  };

  // Final step before persisting: AI safety review, embedded in the record.
  const aiGateThenSave = async d => {
    setUnansweredIds(null);
    setDraft(d);
    setAiGate({
      status: "loading",
      draft: d
    });
    try {
      const r = await aiPost("/api/clinical/ai/consultation-review", {
        trigger: d.trigger,
        components: d.components,
        outcome: d.outcome
      });
      setAiGate({
        status: "ready",
        draft: d,
        result: {
          at: new Date().toISOString(),
          ...r
        }
      });
    } catch (e) {
      setAiGate({
        status: "error",
        draft: d,
        result: {
          at: new Date().toISOString(),
          error: e.message
        }
      });
    }
  };
  const trySave = () => {
    if (!(draft.outcome.notes || "").trim()) {
      setSafetyErr(true);
      appAlert("Safety-netting advice is required before saving — record what the patient was told to watch for and when to seek help.");
      return;
    }
    setSafetyErr(false);
    const empties = draft.components.filter(c => c.label && !(c.value || "").trim()).map(c => c.id);
    if (empties.length) setUnansweredIds(empties);else aiGateThenSave(draft);
  };
  const focusNext = id => {
    const ix = draft.components.findIndex(c => c.id === id);
    for (let j = ix + 1; j < draft.components.length; j++) {
      const el = valRefs.current[draft.components[j].id];
      if (el) {
        el.focus();
        return;
      }
    }
  };
  const labelPool = useMemo(() => {
    const pool = [];
    for (const t of templates) for (const c of t.components) pool.push({
      type: c.type,
      label: c.label,
      from: t.name
    });
    return pool;
  }, [templates]);
  const set = patch => setDraft(d => ({
    ...d,
    ...patch
  }));
  const setComp = (id, patch) => set({
    components: draft.components.map(c => c.id === id ? {
      ...c,
      ...patch
    } : c)
  });
  const addComp = type => set({
    components: [...draft.components, {
      id: uid(),
      type,
      label: "",
      value: "",
      codes: []
    }]
  });
  const suggestions = c => {
    const t = (c.label || "").toLowerCase();
    if (t.length < 3) return [];
    return labelPool.filter(p => p.type === c.type && p.label.toLowerCase().includes(t) && p.label.toLowerCase() !== t).slice(0, 5);
  };
  const saveAsTemplate = async mode => {
    const name = (await appPrompt(mode === "new" ? "Template name:" : "Confirm template to update:", draft.templateName || ""));
    if (!name) return;
    const comps = draft.components.filter(c => c.label).map(c => ({
      type: c.type,
      label: c.label
    }));
    if (mode === "new") {
      const level = (await appPrompt("Level: pharmacist / pharmacy / organisation / global", "pharmacy")) || "pharmacy";
      setTemplates(ts => [...ts, {
        id: uid(),
        name,
        level,
        author: me(),
        targetUsers: level,
        triggerHint: draft.trigger || "",
        version: 1,
        createdAt: now(),
        changeLog: [{
          at: now(),
          by: me(),
          note: "Created from a live consultation"
        }],
        components: comps
      }]);
    } else {
      setTemplates(ts => ts.map(t => t.id === draft.templateId ? {
        ...t,
        components: comps,
        version: (t.version || 1) + 1,
        changeLog: [...(t.changeLog || []), {
          at: now(),
          by: me(),
          note: "Updated from a live consultation"
        }]
      } : t));
    }
    appAlert("Template saved.");
  };
  return <div>
      <div style={{
      position: "sticky",
      top: 0,
      zIndex: 30,
      background: "#fff",
      borderBottom: "1px solid #F0F0F0",
      padding: "8px 0",
      marginBottom: 4
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8
      }}>
        <b style={{
          fontSize: 15
        }}>{draft._isEdit ? "Amending consultation" : "New consultation"} — {patient.firstName} {patient.lastName}</b>
        <div style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap"
        }}>
          <button style={S.btn("#0E8CFF")} onClick={() => saveAsTemplate("new")}>Save as new template</button>
          {draft.templateId && <button style={S.btn("#0E8CFF")} onClick={() => saveAsTemplate("update")}>Update "{draft.templateName}"</button>}
          <button style={S.btn("#5D5D5D")} onClick={onCancel}>Cancel</button>
          <button style={S.btn("#0E8CFF", true)} onClick={trySave}>
            {draft._isEdit ? "Save amendment" : "Save consultation"}</button>
        </div>
      </div>
      <div style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        marginTop: 8
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#5D5D5D",
          alignSelf: "center",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}>Add:</span>
        {Object.entries(TYPES).map(([k, t]) => <button key={k} style={{
          ...S.btn(t.text),
          background: t.bg,
          borderColor: t.border
        }} onClick={() => addComp(k)}>+ {t.label}</button>)}
      </div>
      </div>
      {draft._isEdit && <div style={{
      fontSize: 12,
      color: "#AD4E00",
      background: "#FFFBE6",
      border: "1px solid #FFD666",
      borderRadius: 6,
      padding: "6px 10px",
      margin: "8px 0"
    }}>
        Amending a saved record: the current version will be snapshotted and remain viewable; the record will display on amber as amended.</div>}

      <div style={{
      margin: "8px 0 2px"
    }}>
        {draft.templateId ? <span style={{
        background: "#E6F7FF",
        border: "1px solid #0E8CFF",
        color: "#0E8CFF",
        borderRadius: 6,
        padding: "5px 12px",
        fontSize: 13,
        fontWeight: 800
      }}>
            📋 Template-driven consultation: {draft.templateName}
          </span> : <span style={{
        background: "#F5F5F5",
        border: "1px solid #8C8C8C",
        color: "#434343",
        borderRadius: 6,
        padding: "5px 12px",
        fontSize: 13,
        fontWeight: 800
      }}>
            ✎ Free-text consultation (no template)
          </span>}
      </div>

      <div style={S.lab}>Presenting complaint / what triggered this consultation</div>
      <input style={{
      ...S.input,
      width: "100%"
    }} value={draft.trigger} onChange={e => set({
      trigger: e.target.value
    })} placeholder="e.g. Patient requested ear check — blocked left ear 2 weeks" />

      <div style={S.lab}>Consultation components</div>
      {draft.components.map(c => {
      const t = TYPES[c.type];
      const sugg = suggestFor === c.id ? suggestions(c) : [];
      return <div key={c.id} style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 6,
        padding: "8px 10px",
        marginBottom: 6,
        position: "relative"
      }}>
            <div style={{
          display: "flex",
          gap: 6,
          alignItems: "flex-start"
        }}>
              <span style={{
            ...S.chip("#ffffffaa", t.border, t.text),
            marginTop: 7,
            width: 92,
            textAlign: "center",
            flex: "0 0 auto"
          }}>{t.label}</span>
              <div style={{
            flex: "0 0 25%",
            position: "relative"
          }}>
                <textarea rows={1} style={{
              ...S.input,
              width: "100%",
              minHeight: 32,
              background: "#F5F5F5",
              fontWeight: 700,
              fontStyle: "italic",
              fontFamily: "inherit",
              resize: "vertical"
            }} value={c.label} placeholder={`${t.label} / prompt…`} onChange={e => {
              setComp(c.id, {
                label: e.target.value
              });
              setSuggestFor(c.id);
            }} onBlur={() => setTimeout(() => setSuggestFor(f => f === c.id ? null : f), 200)} />
                {sugg.length > 0 && <div style={{
              position: "absolute",
              zIndex: 5,
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              marginTop: 2
            }}>
                    {sugg.map((p, i) => <div key={i} onMouseDown={() => setComp(c.id, {
                label: p.label
              })} style={{
                padding: "5px 10px",
                fontSize: 12,
                cursor: "pointer"
              }}>{p.label} <span style={{
                  color: "#8C8C8C"
                }}>({p.from})</span></div>)}
                  </div>}
              </div>
              <textarea ref={el => {
            valRefs.current[c.id] = el;
          }} style={{
            ...S.input,
            flex: 1,
            minHeight: 34,
            background: (c.value || "").trim() ? "#F5F5F5" : "#fff",
            fontFamily: "inherit",
            resize: "vertical"
          }} value={c.value} rows={1} placeholder="Findings / answer / detail… (Enter = next; Shift+Enter = new line)" onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              focusNext(c.id);
            }
          }} onChange={e => setComp(c.id, {
            value: e.target.value
          })} />
              <button style={{
            ...S.btn("#CF1322"),
            marginTop: 2
          }} onClick={() => set({
            components: draft.components.filter(x => x.id !== c.id)
          })}>✕</button>
            </div>
            {settings.snomedSuggest && c.type === "assessment" && <div style={{
          marginTop: 4
        }}>
                <button style={S.btn("#1D39C4")} onClick={() => setCodeSuggest(cs => ({
            ...cs,
            [c.id]: snomedSuggest(c.label + " " + c.value)
          }))}>Suggest codes</button>
                {(codeSuggest[c.id] || []).map(([id, term]) => <span key={id} style={{
            ...S.chip("#eef2ff", "#c7d2fe", "#1D39C4"),
            marginLeft: 6,
            cursor: "pointer"
          }} onClick={() => {
            setComp(c.id, {
              codes: [...(c.codes || []), {
                id,
                term
              }]
            });
            setCodeSuggest(cs => ({
              ...cs,
              [c.id]: cs[c.id].filter(x => x[0] !== id)
            }));
          }}>
                    + {term} ({id})</span>)}
                {(c.codes || []).map(cd => <span key={cd.id} style={{
            ...S.chip("#F6FFED", "#B7EB8F", "#389E0D"),
            marginLeft: 6
          }}>✓ {cd.term}</span>)}
              </div>}
          </div>;
    })}
      <div style={S.lab}>Outcome — disposal & diagnoses</div>
      <div style={{
      border: "1px solid #fdba74",
      borderRadius: 6,
      padding: "8px 10px",
      background: "#ffedd5",
      display: "flex",
      gap: 6,
      alignItems: "flex-start",
      flexWrap: "wrap"
    }}>
        <span style={{
        ...S.chip("#ffffffaa", "#fdba74", "#9a3412"),
        marginTop: 6,
        whiteSpace: "nowrap"
      }}>Outcome</span>
        <select style={{
        ...S.input,
        width: 220,
        background: "#fff"
      }} value={draft.outcome.disposal} onChange={e => set({
        outcome: {
          ...draft.outcome,
          disposal: e.target.value
        }
      })}>
          <option value="">Disposal…</option>{DISPOSALS.map(d => <option key={d}>{d}</option>)}
        </select>
        <DiagnosisEditor outcome={draft.outcome} setOutcome={o => set({
        outcome: o
      })} />
        <textarea style={{
        ...S.input,
        flex: "1 1 24%",
        minWidth: 190,
        minHeight: 34,
        fontFamily: "inherit",
        resize: "vertical",
        background: (draft.outcome.notes || "").trim() ? "#F5F5F5" : "#fff",
        border: safetyErr && !(draft.outcome.notes || "").trim() ? "2px solid #CF1322" : "1px solid #8C8C8C"
      }} rows={1} placeholder="Safety-netting advice given (required)…" value={draft.outcome.notes} onChange={e => set({
        outcome: {
          ...draft.outcome,
          notes: e.target.value
        }
      })} />
      </div>

      {aiGate && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1400
    }}>
          <div style={{
        background: "#fff",
        borderRadius: 8,
        width: "min(640px,94vw)",
        maxHeight: "80vh",
        overflowY: "auto",
        padding: 16
      }}>
            <b style={{
          fontSize: 15
        }}>AI safety check before saving</b>
            {aiGate.status === "loading" && <div style={{
          color: "#5D5D5D",
          fontSize: 13,
          padding: "16px 0"
        }}>Reviewing the consultation for safety concerns and overlooked items…</div>}
            {aiGate.status === "ready" && <div style={{
          fontSize: 13,
          marginTop: 8
        }}>
                <div style={{
            background: "#eef2ff",
            border: "1px solid #c7d2fe",
            borderRadius: 6,
            padding: "8px 12px"
          }}>{aiGate.result.summary || "No comments."}</div>
                {aiGate.result.concerns?.length > 0 && <><div style={{
              fontWeight: 800,
              color: "#A8071A",
              marginTop: 8
            }}>Safety concerns</div>
                  {aiGate.result.concerns.map((m, i) => <div key={i}>• {m}</div>)}</>}
                {aiGate.result.overlooked?.length > 0 && <><div style={{
              fontWeight: 800,
              color: "#AD4E00",
              marginTop: 8
            }}>Possibly overlooked</div>
                  {aiGate.result.overlooked.map((m, i) => <div key={i}>• {m}</div>)}</>}
                <div style={{
            color: "#5D5D5D",
            fontSize: 11,
            marginTop: 8
          }}>Advisory only — clinical judgement remains yours. This review is saved as part of the record either way.</div>
              </div>}
            {aiGate.status === "error" && <div style={{
          fontSize: 13,
          marginTop: 8,
          color: "#AD4E00",
          background: "#FFFBE6",
          border: "1px solid #FFD666",
          borderRadius: 6,
          padding: "8px 12px"
        }}>
                AI review unavailable: {aiGate.result.error}. You can continue saving — the unavailability is recorded on the record.
              </div>}
            <div style={{
          display: "flex",
          gap: 8,
          marginTop: 14,
          justifyContent: "flex-end"
        }}>
              <button style={S.btn("#5D5D5D")} disabled={aiGate.status === "loading"} onClick={() => setAiGate(null)}>Return to consultation</button>
              <button style={S.btn("#0E8CFF", true)} disabled={aiGate.status === "loading"} onClick={() => {
            const g = aiGate;
            setAiGate(null);
            doSave(g.draft, g.result);
          }}>Continue — save consultation</button>
            </div>
          </div>
        </div>}

      {unansweredIds && (() => {
      const items = draft.components.filter(c => unansweredIds.includes(c.id));
      const remaining = items.filter(c => !(c.value || "").trim());
      return <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300
      }}>
            <div style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(680px,94vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          padding: 16
        }}>
              <b style={{
            fontSize: 15
          }}>Unanswered items ({remaining.length} of {items.length})</b>
              <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            margin: "4px 0 10px"
          }}>
                Enter responses here, or save with the blanks marked "(not necessary)".
              </div>
              {items.map(c => {
            const t = TYPES[c.type];
            return <div key={c.id} style={{
              background: t.bg,
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              padding: "6px 8px",
              marginBottom: 5,
              display: "flex",
              gap: 6,
              alignItems: "flex-start"
            }}>
                    <span style={{
                ...S.chip("#ffffffaa", t.border, t.text),
                marginTop: 5
              }}>{t.label}</span>
                    <span style={{
                flex: "0 0 36%",
                fontSize: 13,
                fontWeight: 600,
                color: t.text,
                marginTop: 6
              }}>{c.label}</span>
                    <textarea rows={1} style={{
                ...S.input,
                flex: 1,
                minHeight: 32,
                background: "#fff",
                fontFamily: "inherit"
              }} value={c.value} placeholder="Response…" onChange={e => setComp(c.id, {
                value: e.target.value
              })} />
                  </div>;
          })}
              <div style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            justifyContent: "flex-end"
          }}>
                <button style={S.btn("#5D5D5D")} onClick={() => setUnansweredIds(null)}>Cancel</button>
                {remaining.length > 0 && <button style={S.btn("#AD4E00")} onClick={() => {
              const d2 = {
                ...draft,
                components: draft.components.map(c => unansweredIds.includes(c.id) && !(c.value || "").trim() ? {
                  ...c,
                  value: "(not necessary)"
                } : c)
              };
              aiGateThenSave(d2);
            }}>Save & mark empty as not necessary</button>}
                <button style={{
              ...S.btn("#0E8CFF", true),
              opacity: remaining.length ? 0.5 : 1
            }} disabled={remaining.length > 0} onClick={() => aiGateThenSave(draft)}>
                  {remaining.length === 0 ? "Save responses" : `${remaining.length} still unanswered`}
                </button>
              </div>
            </div>
          </div>;
    })()}
    </div>;
}
function DiagnosisEditor({
  outcome,
  setOutcome
}) {
  const [txt, setTxt] = useState("");
  const add = (codes = [], text) => {
    const label = (text || txt).trim();
    if (!label) return;
    setOutcome({
      ...outcome,
      diagnoses: [...(outcome.diagnoses || []), {
        text: label,
        codes
      }]
    });
    setTxt("");
  };
  const live = txt.trim().length >= 3 ? snomedSuggest(txt) : [];
  return <div style={{
    position: "relative",
    flex: "1 1 34%",
    minWidth: 300
  }}>
      <div style={{
      ...S.input,
      background: "#fff",
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      alignItems: "center",
      minHeight: 34,
      padding: "4px 8px"
    }}>
        {(outcome.diagnoses || []).map((d, i) => <span key={i} style={{
        ...S.chip("#eef2ff", "#c7d2fe", "#1D39C4"),
        whiteSpace: "nowrap"
      }}>
            {d.text}{(d.codes || []).length > 0 && ` · ${d.codes[0].id}`}
            <span style={{
          cursor: "pointer",
          color: "#CF1322",
          marginLeft: 5,
          fontWeight: 800
        }} onClick={() => setOutcome({
          ...outcome,
          diagnoses: outcome.diagnoses.filter((_, j) => j !== i)
        })}>✕</span>
          </span>)}
        <input style={{
        border: "none",
        outline: "none",
        fontSize: 13,
        flex: 1,
        minWidth: 110,
        background: "transparent"
      }} placeholder={(outcome.diagnoses || []).length ? "Add another…" : "Diagnosis — type to search codes, Enter to add…"} value={txt} onChange={e => setTxt(e.target.value)} onKeyDown={e => {
        if (e.key === "Enter") {
          e.preventDefault();
          add();
        }
      }} />
      </div>
      {live.length > 0 && <div style={{
      position: "absolute",
      top: "calc(100% + 2px)",
      left: 0,
      width: 300,
      background: "#fff",
      border: "1px solid #D9D9D9",
      borderRadius: 6,
      boxShadow: "0 6px 16px rgba(0,0,0,0.14)",
      zIndex: 40
    }}>
          {live.map(([id, term]) => <div key={id} onMouseDown={() => add([{
        id,
        term
      }], term)} style={{
        padding: "5px 10px",
        fontSize: 12,
        cursor: "pointer",
        borderBottom: "1px solid #F5F5F5"
      }}>
              {term} <span style={{
          color: "#8C8C8C"
        }}>SNOMED {id}</span>
            </div>)}
        </div>}
    </div>;
}
function TemplateManager({
  templates,
  setTemplates
}) {
  const [edit, setEdit] = useState(null);
  const [levelF, setLevelF] = useState("");
  const [aiBusy, setAiBusy] = useState(null); // 'gen' | 'review'
  const [review, setReview] = useState(null); // AI review result
  const [dragIx, setDragIx] = useState(null);
  const aiGenerate = async () => {
    if (!edit.description || edit.description.trim().length < 10) {
      (await appAlert("Type a description of the consultation first — the AI drafts questions from it."));
      return;
    }
    setAiBusy("gen");
    try {
      const d = await aiPost("/api/clinical/ai/template-generate", {
        description: edit.description
      });
      setEdit(t => ({
        ...t,
        components: [...t.components, ...d.components]
      }));
    } catch (e) {
      (await appAlert("AI generation failed: " + e.message));
    } finally {
      setAiBusy(null);
    }
  };
  const aiReview = async () => {
    setAiBusy("review");
    setReview(null);
    try {
      const d = await aiPost("/api/clinical/ai/template-review", {
        name: edit.name,
        description: edit.description,
        components: edit.components.filter(c => c.label)
      });
      setReview(d);
    } catch (e) {
      (await appAlert("AI review failed: " + e.message));
    } finally {
      setAiBusy(null);
    }
  };
  const onDrop = i => {
    if (dragIx === null || dragIx === i) return;
    setEdit(tp => {
      const cs = [...tp.components];
      const [moved] = cs.splice(dragIx, 1);
      cs.splice(i, 0, moved);
      return {
        ...tp,
        components: cs
      };
    });
    setDragIx(null);
  };
  const save = () => {
    setTemplates(ts => {
      const i = ts.findIndex(t => t.id === edit.id);
      const withLog = {
        ...edit,
        version: (edit.version || 0) + 1,
        changeLog: [...(edit.changeLog || []), {
          at: now(),
          by: me(),
          note: i === -1 ? "Created" : "Edited in template manager"
        }]
      };
      return i === -1 ? [...ts, withLog] : ts.map((t, j) => j === i ? withLog : t);
    });
    setEdit(null);
  };
  if (edit) return <div style={{
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: 14,
    background: "#FCFCFC"
  }}>
      <div style={{
      display: "grid",
      gridTemplateColumns: "2fr 1fr 1fr",
      gap: 8
    }}>
        <label style={{
        fontSize: 12
      }}>Name<input style={{
          ...S.input,
          width: "100%"
        }} value={edit.name} onChange={e => setEdit(t => ({
          ...t,
          name: e.target.value
        }))} /></label>
        <label style={{
        fontSize: 12
      }}>Level<select style={{
          ...S.input,
          width: "100%"
        }} value={edit.level} onChange={e => setEdit(t => ({
          ...t,
          level: e.target.value
        }))}>
          {["pharmacist", "pharmacy", "organisation", "global"].map(l => <option key={l}>{l}</option>)}</select></label>
        <label style={{
        fontSize: 12
      }}>Target users<input style={{
          ...S.input,
          width: "100%"
        }} value={edit.targetUsers || ""} onChange={e => setEdit(t => ({
          ...t,
          targetUsers: e.target.value
        }))} /></label>
      </div>
      <label style={{
      fontSize: 12,
      display: "block",
      marginTop: 8
    }}>Trigger hint<input style={{
        ...S.input,
        width: "100%"
      }} value={edit.triggerHint || ""} onChange={e => setEdit(t => ({
        ...t,
        triggerHint: e.target.value
      }))} /></label>
      <label style={{
      fontSize: 12,
      display: "block",
      marginTop: 8
    }}>Description / purpose — what should this consultation cover?
        <textarea style={{
        ...S.input,
        width: "100%",
        minHeight: 50,
        fontFamily: "inherit"
      }} value={edit.description || ""} placeholder="e.g. Assessment and supply for uncomplicated UTI in women 16-64 under the Pharmacy First pathway…" onChange={e => setEdit(t => ({
        ...t,
        description: e.target.value
      }))} /></label>
      <div style={{
      display: "flex",
      gap: 6,
      marginTop: 6
    }}>
        <button style={S.btn("#1D39C4", true)} disabled={!!aiBusy} onClick={aiGenerate}>
          {aiBusy === "gen" ? "Generating…" : "⚡ Generate questions from description (AI)"}</button>
        <button style={S.btn("#1D39C4")} disabled={!!aiBusy || edit.components.filter(c => c.label).length === 0} onClick={aiReview}>
          {aiBusy === "review" ? "Reviewing…" : "🔍 AI review — completeness & clarity"}</button>
      </div>
      {review && <div style={{
      border: "1px solid #c7d2fe",
      background: "#eef2ff",
      borderRadius: 6,
      padding: "8px 12px",
      marginTop: 8,
      fontSize: 13
    }}>
          <b>AI review:</b> {review.summary}
          {review.missing.length > 0 && <><div style={{
          fontWeight: 700,
          marginTop: 4
        }}>Possibly missing:</div>
            {review.missing.map((m, i) => <div key={i}>• {m}</div>)}</>}
          {review.clarity.length > 0 && <><div style={{
          fontWeight: 700,
          marginTop: 4
        }}>Clarity / ordering:</div>
            {review.clarity.map((m, i) => <div key={i}>• {m}</div>)}</>}
          <div style={{
        color: "#5D5D5D",
        fontSize: 11,
        marginTop: 4
      }}>Advisory only — clinical judgement and sign-off remain with you as the author.</div>
        </div>}
      <div style={S.lab}>Components <span style={{
        fontWeight: 400,
        textTransform: "none"
      }}>(drag ⠿ to reorder)</span></div>
      {edit.components.map((c, i) => {
      const t = TYPES[c.type];
      return <div key={i} draggable onDragStart={() => setDragIx(i)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(i)} style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 6,
        padding: "5px 8px",
        marginBottom: 4,
        opacity: dragIx === i ? 0.5 : 1
      }}>
            <span style={{
          cursor: "grab",
          color: "#5D5D5D",
          fontSize: 15,
          userSelect: "none"
        }} title="Drag to reorder">⠿</span>
            <select style={S.input} value={c.type} onChange={e => setEdit(tp => ({
          ...tp,
          components: tp.components.map((x, j) => j === i ? {
            ...x,
            type: e.target.value
          } : x)
        }))}>
              {Object.keys(TYPES).map(k => <option key={k} value={k}>{TYPES[k].label}</option>)}</select>
            <input style={{
          ...S.input,
          flex: 1,
          background: "#fff"
        }} value={c.label} onChange={e => setEdit(tp => ({
          ...tp,
          components: tp.components.map((x, j) => j === i ? {
            ...x,
            label: e.target.value
          } : x)
        }))} />
            <button style={S.btn("#5D5D5D")} disabled={i === 0} onClick={() => setEdit(tp => {
          const cs = [...tp.components];
          [cs[i - 1], cs[i]] = [cs[i], cs[i - 1]];
          return {
            ...tp,
            components: cs
          };
        })}>↑</button>
            <button style={S.btn("#CF1322")} onClick={() => setEdit(tp => ({
          ...tp,
          components: tp.components.filter((_, j) => j !== i)
        }))}>✕</button>
          </div>;
    })}
      <div style={{
      display: "flex",
      gap: 6,
      marginTop: 6,
      flexWrap: "wrap"
    }}>
        {Object.entries(TYPES).map(([k, t]) => <button key={k} style={{
        ...S.btn(t.text),
        background: t.bg,
        borderColor: t.border
      }} onClick={() => setEdit(tp => ({
        ...tp,
        components: [...tp.components, {
          type: k,
          label: ""
        }]
      }))}>+ {t.label}</button>)}
      </div>
      {(edit.changeLog || []).length > 0 && <><div style={S.lab}>Change log</div>
        {edit.changeLog.map((l, i) => <div key={i} style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>{fmtDT(l.at)} — {l.by}: {l.note}</div>)}</>}
      <div style={{
      marginTop: 12,
      display: "flex",
      gap: 8
    }}>
        <button style={S.btn("#0E8CFF", true)} onClick={save}>Save template</button>
        <button style={S.btn("#5D5D5D")} onClick={() => setEdit(null)}>Cancel</button>
      </div>
    </div>;
  return <div>
      <div style={{
      display: "flex",
      gap: 8,
      marginBottom: 10
    }}>
        <select style={S.input} value={levelF} onChange={e => setLevelF(e.target.value)}><option value="">All levels</option>{["pharmacist", "pharmacy", "organisation", "global"].map(l => <option key={l}>{l}</option>)}</select>
        <button style={S.btn("#0E8CFF", true)} onClick={() => setEdit({
        id: uid(),
        name: "",
        level: "pharmacy",
        author: me(),
        targetUsers: "",
        triggerHint: "",
        version: 0,
        createdAt: now(),
        changeLog: [],
        components: []
      })}>+ New template</button>
      </div>
      {templates.filter(t => !levelF || t.level === levelF).map(t => <div key={t.id} style={{
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      padding: "8px 12px",
      marginBottom: 5,
      background: "#fff",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
          <span style={{
        fontSize: 14
      }}><b>{t.name}</b> <span style={S.chip("#F5F5F5", "#D9D9D9", "#434343")}>{t.level}</span>
            <span style={{
          color: "#5D5D5D",
          fontSize: 12,
          marginLeft: 6
        }}>v{t.version} · {t.components.length} components · author {t.author}</span></span>
          <span style={{
        display: "flex",
        gap: 6
      }}>
            <button style={S.btn("#4338ca")} onClick={() => setEdit(JSON.parse(JSON.stringify(t)))}>Edit</button>
            <button style={S.btn("#CF1322")} onClick={async () => (await appConfirm(`Delete template "${t.name}"?`)) && setTemplates(ts => ts.filter(x => x.id !== t.id))}>Delete</button>
          </span>
        </div>)}
    </div>;
}
function Converter({
  onSave
}) {
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(null);
  const convert = () => {
    const lines = text.split(/\n+/).map(l => l.replace(/^[\s•\-\d.)]+/, "").trim()).filter(l => l.length > 8);
    const comps = [];
    for (const l of lines) {
      const low = l.toLowerCase();
      let type = null;
      if (l.includes("?") || /^(does|is|has|have|any|when|what|how|are|do|confirm|check that|ask)/.test(low)) type = "question";else if (/(examin|inspect|otoscop|auscultat|palpat|measure|observe|temperature|blood pressure|look (at|in)|visualis)/.test(low)) type = "examination";else if (/(assess|diagnos|inclusion|exclusion|criteri|differenti|red flag|contraindicat|caution)/.test(low)) type = "assessment";else if (/(supply|administer|refer|advise|record|document|prescrib|issue|give|counsel|safety.?net|signpost|write|send)/.test(low)) type = "action";
      if (type) comps.push({
        type,
        label: l.length > 140 ? l.slice(0, 140) + "…" : l
      });
    }
    setDraft({
      id: uid(),
      name: "",
      level: "pharmacy",
      author: me(),
      targetUsers: "",
      triggerHint: "",
      version: 1,
      createdAt: now(),
      changeLog: [{
        at: now(),
        by: me(),
        note: "Drafted by document converter"
      }],
      components: comps.slice(0, 30)
    });
  };
  return <div>
      <div style={{
      fontSize: 13,
      color: "#434343",
      marginBottom: 8
    }}>
        Paste the text of a PGD, service specification, or procedure (e.g. an ear irrigation SOP). Lines are classified into
        Questions / Examinations / Assessments / Actions heuristically — review the draft before saving.
        (Runs locally; this same screen will call the platform AI for smarter extraction when the backend is wired.)
      </div>
      <textarea style={{
      ...S.input,
      width: "100%",
      minHeight: 180,
      fontFamily: "inherit"
    }} value={text} onChange={e => setText(e.target.value)} placeholder="Paste document text here…" />
      <div style={{
      margin: "8px 0"
    }}><button style={S.btn("#0E8CFF", true)} onClick={convert} disabled={text.trim().length < 40}>Convert to draft template</button></div>
      {draft && <div style={{
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      padding: 12,
      background: "#FCFCFC"
    }}>
          <input style={{
        ...S.input,
        width: 320
      }} placeholder="Template name…" value={draft.name} onChange={e => setDraft(d => ({
        ...d,
        name: e.target.value
      }))} />
          <div style={{
        margin: "8px 0"
      }}>{draft.components.map((c, i) => {
          const t = TYPES[c.type];
          return <div key={i} style={{
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 6,
            padding: "4px 8px",
            marginBottom: 3,
            fontSize: 13
          }}>
              <span style={{
              ...S.chip("#ffffffaa", t.border, t.text),
              marginRight: 6
            }}>{t.label}</span>{c.label}
              <span style={{
              float: "right",
              cursor: "pointer",
              color: "#CF1322"
            }} onClick={() => setDraft(d => ({
              ...d,
              components: d.components.filter((_, j) => j !== i)
            }))}>✕</span>
            </div>;
        })}</div>
          <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 6
      }}>{draft.components.length} components extracted — remove any noise, save, then refine in the template editor.</div>
          <button style={S.btn("#0E8CFF", true)} disabled={!draft.name.trim()} onClick={() => onSave(draft)}>Save draft template</button>
        </div>}
    </div>;
}
